import { z } from 'zod';
/**
 * Conflict and overwrite decisions for AIMSIS-sourced academic reference and
 * relationship data.
 *
 * One planned action per affected record. The action shown before apply must
 * match the action reported after apply, so this model is produced by the same
 * pure planner in both cases (see api/src/lib/sync-decisions.ts).
 *
 * This is the shared vocabulary between the sync preview (AIMSIS-18806) and the
 * apply operation (AIMSIS-18807) — neither may invent its own action names.
 */
export const SyncDecisionAction = z.enum([
    'create',
    'update',
    'keep',
    'inactivate',
    'archive',
    'conflict',
    'skip',
]);
export const SyncRecordType = z.enum([
    'gradeLevel',
    'subject',
    'classroom',
    'classMembership',
    'teachingAssignment',
]);
/**
 * Stable machine-readable reasons. The UI renders its own copy per code; the
 * code itself never changes wording, so history stays comparable across runs.
 */
export const SyncDecisionReason = z.enum([
    /** No CBT record and no external ID collision — a new record is created. */
    'new_record',
    /**
     * Matched by external ID, and the write would change the document — either a
     * source-owned field differs, or the record is missing a derived field the
     * destination reads by (see `changedFields`), or its identity/ownership marker
     * is being stamped. The action is `update` in every one of those cases.
     */
    'source_field_changed',
    /** Matched by external ID; the document already agrees and needs no write. */
    'no_change',
    /** Present in AIMSIS, but the external ID is absent. */
    'missing_external_id',
    /** The same external ID appears on more than one source row. */
    'duplicate_external_id',
    /** A required source field is empty or malformed. */
    'invalid_source_record',
    /** The external ID already belongs to a CBT record that is not its match. */
    'ambiguous_external_id',
    /**
     * The row's source identity resolves to more than one CBT record, or is
     * carried by more than one source row. Covers the identity a record type is
     * matched by when the source has no single id for it — an enrollment is
     * identified by its resolved user, classroom and start date, and two records
     * (or two rows) claiming that identity cannot both be honoured.
     */
    'ambiguous_source_identity',
    /** A CBT record is integrated and carries this external ID but a different identity. */
    'external_id_owned_by_other_record',
    /**
     * One CBT document is claimed by two source identities at once — its canonical
     * document id encodes one identity while its stored external-ID field names
     * another. Neither claim can be honoured without losing the other.
     */
    'conflicting_identity_claims',
    /**
     * A responsibility an administrator deliberately closed. The source keeps
     * supplying the row, so the closure is preserved and the disagreement is
     * reported rather than reopening the record on every run.
     */
    'closed_responsibility_conflict',
    /**
     * A closed responsibility the source supplies with the same identity. Nothing
     * is written: the closure owns the record's range and status, and re-applying
     * the source's values would reopen it.
     */
    'closed_responsibility_preserved',
    /** No longer supplied by AIMSIS; the record type ends by inactivation. */
    'absent_from_source',
    /** No longer supplied by AIMSIS; the record type ends by archival. */
    'absent_from_source_archived',
    /** The record was already retired; nothing further to do. */
    'already_retired',
    /** A relationship references a User or Classroom unknown to CBT. */
    'unknown_reference',
    /** Effective dates are inverted or otherwise unusable. */
    'invalid_effective_dates',
    /** CBT-only record with no external ID — never touched by sync. */
    'local_only_record',
    /** AIMSIS is not configured for this tenant; local data stays usable. */
    'standalone_mode',
]);
/**
 * The CBT-side values a decision was taken against, so an administrator can see
 * what would change. Absent for a record with no CBT counterpart.
 */
export const SyncDecisionExistingValue = z.object({
    internalId: z.string().min(1).max(256),
    status: z.string().max(64).nullable(),
    values: z.record(z.string(), z.unknown()),
});
export const SyncDecisionRecord = z.object({
    recordType: SyncRecordType,
    /** Stable key for this decision within a run (external ID, else internal ID). */
    key: z.string().min(1).max(256),
    /** Readable name, or a relationship summary for memberships/assignments. */
    name: z.string().max(400),
    action: SyncDecisionAction,
    reason: SyncDecisionReason,
    /**
     * Fields the write would change, for `update` decisions. Covers source-owned
     * fields that differ, the external-ID/ownership stamps, and any derived field
     * (a lower-cased index, an ordering) the record is missing — so a non-empty
     * list means a write is proposed, and an empty one always means `keep`.
     */
    changedFields: z.array(z.string().min(1).max(64)),
    internalId: z.string().max(256).nullable(),
    externalId: z.string().max(256).nullable(),
    existing: SyncDecisionExistingValue.nullable(),
    /** Incoming AIMSIS values, when the source supplied the record. */
    incoming: z.record(z.string(), z.unknown()).nullable(),
    /** Relationship records only — ISO `YYYY-MM-DD`. */
    effectiveFrom: z.string().max(32).nullable(),
    effectiveTo: z.string().max(32).nullable(),
    /** Validation error or conflict explanation for conflict/skip decisions. */
    message: z.string().max(500).nullable(),
});
/**
 * The complete plan for one entity type.
 *
 * `proposed` is what the plan would write, and the planner always fills it in.
 * `written` counts what was actually persisted, so the planner leaves it at 0 —
 * it never persists anything. Only the apply operation (AIMSIS-18807) raises it,
 * once its writes have committed. A preview (AIMSIS-18806) therefore reports
 * `written: 0` with a non-zero `proposed`, which is the honest answer: nothing
 * was written, but this much would be.
 */
export const SyncDecisionPlan = z.object({
    recordType: SyncRecordType,
    /** False when AIMSIS is not configured — every record is then `keep`. */
    integrated: z.boolean(),
    counts: z.object({
        fetched: z.number().int().nonnegative(),
        create: z.number().int().nonnegative(),
        update: z.number().int().nonnegative(),
        keep: z.number().int().nonnegative(),
        inactivate: z.number().int().nonnegative(),
        archive: z.number().int().nonnegative(),
        conflict: z.number().int().nonnegative(),
        skip: z.number().int().nonnegative(),
    }),
    /** Records the plan would write. Filled in by the planner, preview or apply. */
    proposed: z.number().int().nonnegative(),
    /**
     * Records actually persisted. Always 0 from the planner — it does not write;
     * the apply operation sets this to what it committed.
     */
    written: z.number().int().nonnegative().default(0),
    records: z.array(SyncDecisionRecord),
});
//# sourceMappingURL=sync-decision.js.map